import axios from "axios";
import { LOCAL_STORAGE_KEYS } from "@maxis/client-logic/constants/enumerates";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from "@maxis/client-logic/utils/localStorageUtil";
import { encryptAES, decryptAES } from "@maxis/client-logic/utils/cryptoUtil";
import { completeServerUrl } from "@maxis/client-logic/utils/urlUtil";
import {
  createApiError,
  getApiResponseCode,
  logSafeApiFailure,
} from "@maxis/client-logic/apis/apiErrorSafety.mjs";
import {
  LONG_REQUEST_TIMEOUT_MS,
  REQUEST_TIMEOUT_MS,
  TIMEOUT_MESSAGE,
  isTimeoutError,
  withTimeout,
} from "@maxis/client-logic/utils/requestTimeoutUtil";

// 환경 변수 상수로 관리
const API_SERVER_URL =
  completeServerUrl(import.meta.env.VITE_API_SERVER_URL) + "/api";
const AUTH_ENCRYPTION = import.meta.env.VITE_AUTH === "true";

const handleEncryption = async (data) => {
  if (AUTH_ENCRYPTION) {
    let Encrypt = {};
    const { encryptData, gIv: iv } = await encryptAES(JSON.stringify(data));
    Encrypt.EncryptData = encryptData;
    Encrypt.iv = iv;

    return JSON.stringify(Encrypt);
  }
  return JSON.stringify(data);
};

const createSettings = async (options) => {
  // const body = await handleEncryption(options.body || {});
  return {
    headers: { "Content-Type": "application/json", ...options.headers },
    method: options.method || "POST",
    ...(options.body && { body: await handleEncryption(options.body) }),
  };
};

const createFileUploadSettings = async (options) => {
  const form = new FormData();
  const param = await handleEncryption(options.body || {});
  form.append("param", new Blob([param], { type: "application/json" }));
  for (const file of options.attachFiles) {
    form.append("files", file);
  }
  return {
    headers: { ...options.headers },
    method: options.method || "POST",
    body: form,
  };
};

const downloadFileChunks = async (url, settings, fileName) => {
  const chunks = [];
  // 청크마다 새 상한을 건다. 전체 다운로드에 하나만 걸면 큰 파일이 잘린다.
  const fetchChunk = async (chunkSettings) => {
    const { settings: bounded, clear } = withTimeout(
      chunkSettings,
      LONG_REQUEST_TIMEOUT_MS
    );
    try {
      return await fetch(url, bounded);
    } finally {
      clear();
    }
  };

  const response = await fetchChunk(settings);
  const contentRange = response.headers.get("Content-Range");
  const fileSize = parseInt(contentRange.split("/")[1]);
  const chunkSize = parseInt(response.headers.get("Content-Length"));

  let start = 0;

  while (start < fileSize) {
    const end = Math.min(start + chunkSize - 1, fileSize - 1);
    settings.headers.set("Range", `bytes=${start}-${end}`);
    const chunkResponse = await fetchChunk(settings);
    chunks.push(await chunkResponse.arrayBuffer());
    start = end + 1;
  }

  const combinedBlob = new Blob(chunks);
  const downloadLink = document.createElement("a");
  downloadLink.href = URL.createObjectURL(combinedBlob);
  downloadLink.download = fileName || "downloaded_file";
  downloadLink.click();
};

const handleError = (error) => {
  logSafeApiFailure("ApiFetch", error);
  if (isTimeoutError(error)) {
    const timeoutError = new Error(TIMEOUT_MESSAGE);
    timeoutError.name = "RequestTimeout";
    throw timeoutError;
  }
  if (
    ["TokenAuthError", "TokenAuthExpired", "overLapLogIn"].includes(error.name)
  ) {
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.COMMON_CODES);
    setTimeout(() => {
      window.location.href = import.meta.env.VITE_PUBLIC_URL + "/login";
    }, 1500);
  }
  error.name = "err";
  throw error;
};

/**
 * 응답 데이터 파싱
 *
 * @async
 * @param {Response} response - API 요청에 대한 응답 객체
 * @returns {Promise<Object>} 복호화된 JSON 데이터 객체
 */
const parseResponse = async (response, tran = false) => {
  const packages = tran ? response.data : await response.json();
  let result = "";
  if (AUTH_ENCRYPTION) {
    result = JSON.parse(await decryptAES(packages.EncryptData, packages.iv));
  } else {
    result = packages;
  }
  return result;
};

/**
 * 파일 다운로드 처리
 *
 * @async
 * @param {string} url - 파일 다운로드 요청을 보낼 URL
 * @param {Object} settings - 요청에 사용할 fetch 옵션 및 헤더 정보
 * @param {Response} response - 서버로부터 최초 수신한 응답 객체
 * @returns {Promise<void>}
 */
const handleDownload = async (url, settings, response) => {
  const fileName = response.headers
    .get("Content-Disposition")
    ?.match(/filename="(.+)"/)?.[1];
  // 원 요청의 signal 은 이미 시간을 쓴 상태다. 다운로드는 자체 상한을 받는다.
  const { signal, ...rest } = settings;
  await downloadFileChunks(url, rest, fileName);
};

/**
 * API 응답 데이터 처리
 *
 * @async
 * @param {Response} response - API 응답 객체
 * @returns {Promise<Object>} API 응답 데이터 중 data 필드
 * @throws {Error} API 응답이 실패했거나, 응답 코드가 비정상적인 경우 발생
 */
const handleApiResponse = async (response, tran = false) => {
  const json = await parseResponse(response, tran);
  if (json.responseCode === "0000") {
    return json.data;
  } 

  await exceptionToken(json);
  
  if (tran) {
    const { responseCode = null, responseText = null, data = null } = json;
    if (responseCode) {
      throw createApiError(responseCode, responseText, "API 호출 오류 발생", data);
    }
    return json;
  }

  throw createApiError(
    json.responseCode,
    json.responseText,
    "API 호출 오류 발생",
    json.data
  );
};

const exceptionToken = async ({ responseCode = '', responseText = ''}) => { 
  let err = createApiError(responseCode, responseText, "API 호출 오류 발생");
  if (responseCode === "9006") {
    err.name = "multiValidation";
    throw err;
  } else if (responseCode === "9000") {
    err.name = "TokenAuthError";
    throw err;
  } else if (responseCode === "9002") {
    err.name = "TokenAuthExpired";
    throw err;
  } else if (responseCode === "9003") {
    err.name = "overLapLogIn";
    throw err;
  }
}

/**
 * 설정 객체(settings)에 Authorization 헤더(토큰)를 추가
 *
 * @param {Object} settings - fetch 요청 옵션 (헤더가 포함됨)
 */
const setAuthorizationHeader = (settings) => {
  const accessToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  if (accessToken) {
    settings.headers["Authorization"] = `Bearer ${accessToken}`;
  }
};

/**
 * API 요청
 */
const ApiFetch = {
  request: async (url, options = {}) => {
    const settings = options.attachFiles
      ? await createFileUploadSettings(options)
      : await createSettings(options);
    setAuthorizationHeader(settings);

    // 업로드는 일반 호출보다 상한이 길다.
    const { settings: bounded, clear } = withTimeout(
      settings,
      options.attachFiles ? LONG_REQUEST_TIMEOUT_MS : REQUEST_TIMEOUT_MS
    );

    try {
      const response = await fetch(API_SERVER_URL + url, bounded);

      if (response.status === 206) {
        await handleDownload(API_SERVER_URL + url, bounded, response);
        return;
      }

      if (response.ok) {
        return await handleApiResponse(response);
      }

      if (response.status !== 204) {
        return await handleApiResponse(response);
      }

      throw new Error("알 수 없는 API 호출 오류 발생");
    } catch (error) {
      handleError(error);
    } finally {
      clear();
    }
  },
};

// Axios 인스턴스 설정
const BASE_URL = completeServerUrl(import.meta.env.VITE_API_SERVER_URL) + "/";

// timeout 은 반드시 양수여야 한다. 이전 값 -1 은 axios 에서 "제한 없음"으로
// 동작해, 백엔드가 멎으면 화면이 무한 로딩에 머물렀다.
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

// Authorization 토큰 추가
apiClient.interceptors.request.use((config) => {
  const token = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

//거래 아이디
const generateTransactionId = (systemPrefix = "MAXIS") => {
  const now = new Date();
  const timestamp = now
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 17); // YYYYMMDDHHMMSSsss
  const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase(); // 랜덤 4자리
  return `${systemPrefix}-${timestamp}-${randomStr}`;
};

const detectDeviceType = () => {
  const userAgent = navigator.userAgent;

  if (/mobile/i.test(userAgent)) {
    return "MOBILE"; // 모바일 디바이스
  } else if (/tablet/i.test(userAgent)) {
    return "TABLET"; // 태블릿 디바이스
  } else {
    return "WEB"; // 데스크톱 또는 기타
  }
};

// 공통 JSON 전문 구조 조립 함수
const buildRequestEnvelope = async (data = {}) => {
  const authEncryption = import.meta.env.VITE_AUTH === "true";

  const elements = [];
  const body = {};
  const headerOverrides = {};

  // JSON 데이터 자동 분류
  Object.entries(data).forEach(([key, value]) => {
    if (key === "serviceId" || key === "process") {
      headerOverrides[key] = value; // header에 들어갈 값
    } else if (Array.isArray(value)) {
      elements.push(...value); // 리스트 데이터를 elements에 추가
      body[key] = value;
    } else {
      body[key] = value; // 단건 속성을 body에 추가
    }
  });

  let envelope = {
    envelope: {
      header: {
        transactionId: generateTransactionId(),
        reqDtti: new Date()
          .toISOString()
          .replace(/[-:T.Z]/g, "")
          .slice(0, 14),
        cliIp: "",
        sysCd: "",
        menuId: "",
        gramId: "",
        dvcTp: detectDeviceType(),
        serviceId: headerOverrides.serviceId || "",
        process: headerOverrides.process || [],
      },
      body: {
        elements, // 리스트 데이터
        ...body, // 단건 데이터
      },
    },
  };

  // 암호화 여부에 따라 처리
  if (authEncryption) {
    envelope = await handleEncryption(envelope);
  }

  return envelope;
};

export const DelegateFetch = {
  /**
   * 단일 API 거래 함수
   * @param {Object} options - 요청 데이터와 메타 정보
   * @param {Boolean} useEnvelope - 전문 구조 사용 여부
   */
  tran: async ({ useEnvelope = true, ...options }) => {
    try {
      const baseUrl = completeServerUrl(import.meta.env.VITE_API_SERVER_URL);

      // 요청 데이터 암호화 및 조립
      const requestData = useEnvelope
        ? await buildRequestEnvelope(options)
        : options;

      // console.log("Request Data:", requestData);

      // 파일 업로드 처리
      if (options.attachFiles) {
        const formData = new FormData();
        formData.append(
          "param",
          new Blob([await handleEncryption(requestData)], {
            type: "application/json",
          })
        );
        for (const file of options.attachFiles) {
          formData.append("files", file);
        }

        const response = await apiClient.post(
          options.url || "/file-upload",
          formData
        );
        return response.data;
      }

      // 일반 API 요청
      const response = await apiClient.post(baseUrl + "/tran", requestData);

      // 응답 데이터 반환
      return await handleApiResponse(response, true);
    } catch (error) {
      // 공통 에러 처리
      logSafeApiFailure("DelegateFetch", error);

      const safeResponseCode = getApiResponseCode(error);
      if (!error.responseCode && safeResponseCode) {
        error.responseCode = safeResponseCode;
      }

      // 토큰 관련 에러 처리
      if (
        ["9000", "9002", "9003", "9006"].includes(safeResponseCode)
      ) {
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.COMMON_CODES);
        window.location.href = import.meta.env.VITE_PUBLIC_URL + "/login";

        await exceptionToken({
          responseCode: safeResponseCode,
          responseText: error.message,
        });
      }


      // 에러 다시 던지기
      throw error;
    }
  },
};

export default ApiFetch;
